// Client-side User-Agent detection for the onboarding handoff.
//
// Mirrors the server classifier (src/shared/user-agent/ua-classify.js): the
// server stamps every web-event for analytics, while these run in the SPA to
// adapt the handoff UX live — chiefly to surface an "open in Safari/Chrome"
// hint when the sms: deep link fails inside a Facebook/Instagram webview.
//
// Best-effort markers, not a full UA parser. SSR-safe (guards `navigator`).

function ua(): string {
  return typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
}

// True for the in-app browsers that dominate paid-social traffic, where the
// sms: scheme is most likely to be blocked or no-op.
export function isInAppWebview(userAgent = ua()): boolean {
  const s = userAgent;
  if (!s) return false;
  return /\bFBAN\b|\bFBAV\b|FB_IAB|FB4A|FBIOS|Instagram|BytedanceWebview|musical_ly|\bTikTok\b|Aweme|Snapchat|MicroMessenger|\bLine\/|Twitter|\bX\/|LinkedInApp|Reddit|Telegram|Discord|Pinterest|GSA\/|Gmail|; wv\)/i.test(s);
}

/**
 * Environments where firing `sms:` is known to do harm rather than nothing.
 *
 * The door's normal posture is to fire the deep link and watch (see
 * inbound-door.ts): most in-app browsers hand the tap to Messages, and the
 * ones that silently drop it cost the person two seconds and land on the
 * fallback. Instagram on iOS is neither — the tap RELOADS THE DOCUMENT.
 * Measured on prod over 2026-08-31 → 09-16: six attempts, zero arrivals, and
 * three of the six show a fresh page.viewed + page.rendered 4–11s after the
 * tap (the other three never reported again). The reload restarts the SPA, so
 * the wait comes back as the minimized pill and the sheet the person was
 * reading is gone; every one of them wandered off to something else. Worse,
 * the reload fires `pagehide`, which the reach watcher reads as "Messages
 * opened" — so the fallback never even offers itself.
 *
 * Android Instagram is deliberately NOT here: it fires sms: correctly (2/2 on
 * prod since the 09-09 fixes). This is one combination, named from evidence,
 * not a family. Anything added here must come with its own prod numbers.
 */
export function smsDeepLinkUnsupported(userAgent = ua()): boolean {
  const s = userAgent;
  if (!s) return false;
  return /iPhone|iPad|iPod/i.test(s) && /Instagram/i.test(s);
}

export type OsFamily = 'ios' | 'android' | 'mac' | 'other';

export function getOs(): OsFamily {
  const s = ua();
  if (/iPhone|iPad|iPod/i.test(s)) return 'ios';
  if (/Android/i.test(s)) return 'android';
  // macOS is its own family because, unlike Windows/Linux, a Mac paired with
  // an iPhone (Continuity / Text Message Forwarding) DOES launch Messages.app
  // from an sms: link — so the handoff should fire the deep link + watch,
  // not short-circuit straight to the copyable fallback. (iPadOS desktop-mode
  // Safari reports as Macintosh too; it can open Messages, so 'mac' fits there.)
  if (/Macintosh|Mac OS X/i.test(s)) return 'mac';
  return 'other';
}
